/**
 * V2 Step 1 — Account sign-in.
 * Custom UI: Google OAuth, and — when enabled — an OR divider and a single
 * auto-detect email/US phone field.
 * Three views: form → sent → signedin.
 * Auth success is handled by BusinessSignupV2's useEffect on `user` (auto-advances to step 2).
 *
 * Email magic-link and phone OTP are currently HIDDEN on this screen, not
 * removed: this step now sits straight after the preview builder, where the
 * visitor has just claimed a link, so the goal is the shortest path to a
 * session. The implementation below (handleSend, handleVerifyOtp, the `sent`
 * view, and the identifier helpers) is intact and comes back by flipping
 * SHOW_EMAIL_PHONE_AUTH — or per-render via the showEmailPhoneAuth prop, which
 * the tests use to keep covering that path.
 */
import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { V2C, fontBody } from '../shared/tokens';
import { V2Input, V2Btn, V2GhostBtn, V2InlineMsg, GoogleGlyph, SpinnerIcon } from '../shared/primitives';
import V2Step from '../shared/V2Step';
import { sanitizedErrorProperties } from '../onboardingFlow';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { trackEvent } from '@/lib/posthog';
import { trackPixelEvent } from '@/lib/meta-pixel';
import type { BusinessSignupData } from '@/components/onboarding/magic-steps/BusinessSignup';

interface Props {
  formData: BusinessSignupData;
  update: (patch: Partial<BusinessSignupData>) => void;
  onBack?: () => void;
  stepIdx: number;
  totalSteps: number;
  animDir: 'forward' | 'back';
  validationError: string;
  onNext: () => void;
  /** Overrides SHOW_EMAIL_PHONE_AUTH for a single render. Tests only. */
  showEmailPhoneAuth?: boolean;
}

/**
 * Show the email magic-link / phone OTP alternatives on this step.
 * Off while the preview-first flow is being measured — Google alone keeps the
 * post-claim step to one decision. Both paths remain fully available at
 * /fancy-signin and in the shared auth components.
 */
const SHOW_EMAIL_PHONE_AUTH: boolean = false;

type View = 'form' | 'sent' | 'signedin';
type IdKind = 'email' | 'phone' | 'unknown' | 'invalid';

function formatUS(s: string): string {
  const d = s.replace(/[^0-9]/g, '').replace(/^1/, '').slice(0, 10);
  if (!d) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function toE164US(s: string): string {
  const d = s.replace(/[^0-9]/g, '');
  if (d.length === 10) return '+1' + d;
  if (d.length === 11 && d.startsWith('1')) return '+' + d;
  return s;
}

function detectKind(raw: string): IdKind {
  if (!raw) return 'unknown';
  if (raw.includes('@')) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(raw) ? 'email' : 'invalid';
  }
  if (/^[+()\-.\s\d]+$/.test(raw)) {
    const d = raw.replace(/[^0-9]/g, '');
    if (d.length === 10) return 'phone';
    if (d.length === 11 && d.startsWith('1')) return 'phone';
    if (d.length > 0 && d.length < 10) return 'unknown';
    return 'invalid';
  }
  return 'invalid';
}

function authErrorMessage(error: unknown, fallback: string): string {
  const message = (error as { message?: unknown })?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

export default function V2AccountStep({
  formData, update, stepIdx, totalSteps, animDir, onNext,
  showEmailPhoneAuth = SHOW_EMAIL_PHONE_AUTH,
}: Props) {
  const { user } = useAuth();
  const [view, setView] = useState<View>(user ? 'signedin' : 'form');
  const [input, setInput] = useState(formData.email || '');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentChannel, setSentChannel] = useState<'email' | 'phone'>('email');
  const [sentTo, setSentTo] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const signupStartedFired = useRef(false);

  /**
   * Funnel telemetry is deliberately fail-open. Privacy-restricted WebViews can
   * throw on sessionStorage, and third-party pixel code can throw synchronously;
   * neither is allowed to prevent the actual Supabase auth call.
   */
  function fireSignupStarted(cta_location: string) {
    if (signupStartedFired.current) return;
    signupStartedFired.current = true;
    const now = Date.now();

    try {
      // Store timestamp for time_to_signup_seconds calculation in AuthContext.
      sessionStorage.setItem('figgy_signup_started_at', String(now));
    } catch {
      trackEvent('signup_tracking_degraded', {
        cta_location,
        unavailable_dependency: 'session_storage',
      });
    }

    trackEvent('signup_started', {
      entry_path: window.location.pathname,
      cta_location,
    });

    try {
      trackPixelEvent('Lead');
    } catch {
      trackEvent('signup_tracking_degraded', {
        cta_location,
        unavailable_dependency: 'meta_pixel',
      });
    }
  }

  function trackAuthFailure(authMethod: string, authError: unknown) {
    trackEvent('signup_auth_failed', {
      auth_method: authMethod,
      ...sanitizedErrorProperties(authError),
    });
  }

  const raw = input.trim();
  const idKind = detectKind(raw);

  // Auto-advance to signedin view when session appears
  useEffect(() => {
    if (user && view !== 'signedin') setView('signedin');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  function onInputChange(v: string) {
    setError(null);
    if (v.includes('@') || /[a-zA-Z]/.test(v)) { setInput(v); return; }
    if (!v) { setInput(''); return; }
    if (/^[+()\-.\s\d]*$/.test(v)) { setInput(formatUS(v)); return; }
    setInput(v);
  }

  async function handleSend() {
    setError(null);
    if (!raw || idKind === 'unknown') { setError('Enter your email or phone number.'); return; }
    if (idKind === 'invalid') {
      setError(raw.includes('@') ? 'Please enter a valid email address.' : 'Please enter a valid US phone number.');
      return;
    }

    fireSignupStarted('signup_form');
    setLoading(true);
    try {
      if (idKind === 'email') {
        const { error: e } = await supabase.auth.signInWithOtp({
          email: raw,
          options: { emailRedirectTo: `${window.location.origin}/magic-onboarding-v2` },
        });
        if (e) throw e;
        update({ email: raw });
        setSentChannel('email');
        setSentTo(raw);
        setView('sent');
      } else {
        const phone = toE164US(raw);
        const { error: e } = await supabase.auth.signInWithOtp({ phone });
        if (e) throw e;
        setSentChannel('phone');
        setSentTo(phone);
        setView('sent');
      }
    } catch (e: unknown) {
      trackAuthFailure(idKind === 'email' ? 'email_magic_link' : 'phone_otp', e);
      setError(authErrorMessage(e, 'Failed to send — please try again.'));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp() {
    if (!otpCode.trim()) { setVerifyError('Enter the code from your text.'); return; }
    setVerifyLoading(true);
    setVerifyError(null);
    try {
      const { error: e } = await supabase.auth.verifyOtp({
        phone: sentTo,
        token: otpCode.trim(),
        type: 'sms',
      });
      if (e) throw e;
      // user state updates via AuthContext → BusinessSignupV2 useEffect auto-advances
    } catch (e: unknown) {
      trackAuthFailure('phone_otp_verify', e);
      setVerifyError(authErrorMessage(e, 'Invalid code. Try again.'));
    } finally {
      setVerifyLoading(false);
    }
  }

  async function handleGoogle() {
    setError(null);
    setGoogleLoading(true);
    fireSignupStarted('google_oauth');

    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/magic-onboarding-v2` },
      });
      if (oauthError) throw oauthError;

      trackEvent('signup_oauth_redirect_started', {
        auth_method: 'google_oauth',
      });
    } catch (oauthError: unknown) {
      trackAuthFailure('google_oauth', oauthError);
      setError(authErrorMessage(oauthError, 'Google sign-in could not start. Please try again.'));
      setGoogleLoading(false);
    }
  }

  const detectedPill = (idKind === 'email' || idKind === 'phone') ? (
    <span style={{
      fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase',
      color: V2C.brand, background: V2C.brandSoft, padding: '3px 7px', borderRadius: 99,
      whiteSpace: 'nowrap',
    }}>{idKind === 'email' ? 'Email' : 'US Phone'}</span>
  ) : null;

  return (
    <V2Step
      stepIdx={stepIdx}
      totalSteps={totalSteps}
      title="Save your page"
      subtitle="Continue to publish your booking page and finish setup."
      animDir={animDir}
    >
      {view === 'signedin' ? (
        <>
          {/* Signed-in block */}
          <div style={{
            background: '#fff', borderRadius: 16, border: `1px solid ${V2C.lineHard}`,
            padding: 16, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 99,
              background: `linear-gradient(135deg, ${V2C.brand}, ${V2C.accent})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0,
            }}>
              {(user?.email || user?.phone || 'U')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: V2C.ink, fontFamily: fontBody }}>
                Successfully authenticated
              </div>
              <div
                className="ph-no-capture"
                style={{
                  fontSize: 12, color: V2C.mute, fontFamily: fontBody,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {user?.email || user?.phone}
              </div>
            </div>
            <span style={{
              fontSize: 11, fontWeight: 700, color: V2C.ok, background: V2C.okBg,
              padding: '4px 8px', borderRadius: 99, flexShrink: 0,
            }}>Verified</span>
          </div>

          <V2Btn onClick={onNext}>Continue</V2Btn>
          <V2GhostBtn
            onClick={async () => {
              try {
                await supabase.auth.signOut();
                setView('form');
                setInput('');
              } catch (signOutError: unknown) {
                trackAuthFailure('sign_out', signOutError);
                setError(authErrorMessage(signOutError, 'Could not switch accounts. Please try again.'));
              }
            }}
            style={{ marginTop: 4 }}
          >
            Use a different account
          </V2GhostBtn>
        </>
      ) : view === 'sent' ? (
        <>
          {/* Sent / waiting state */}
          <div style={{
            background: V2C.brandSoft, border: `1px solid ${V2C.brand}33`, borderRadius: 14,
            padding: 18, textAlign: 'center', marginBottom: 14,
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 99, background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 10px', border: `1px solid ${V2C.brand}33`,
            }}>
              {sentChannel === 'email' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={V2C.brand} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="14" rx="2"/>
                  <path d="m3 7 9 6 9-6"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={V2C.brand} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6" y="2" width="12" height="20" rx="2"/>
                  <line x1="12" y1="18" x2="12" y2="18"/>
                </svg>
              )}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: V2C.ink, marginBottom: 4, fontFamily: fontBody }}>
              {sentChannel === 'email'
                ? 'Check your email and click the verification link'
                : 'Check your texts for a verification code'}
            </div>
            <div className="ph-no-capture" style={{ fontSize: 12, color: V2C.mute, fontFamily: fontBody }}>Sent to {sentTo}</div>
            {sentChannel === 'email' && (
              <div style={{
                marginTop: 10, fontSize: 11.5, color: V2C.brand, fontWeight: 600, fontFamily: fontBody,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <span style={{
                  width: 12, height: 12, borderRadius: 99,
                  border: `2px solid ${V2C.brand}33`, borderTopColor: V2C.brand,
                  animation: 'v2spin 0.9s linear infinite', display: 'inline-block',
                }}/>
                This page will continue automatically once you click the link
              </div>
            )}
          </div>

          {/* Phone OTP code entry */}
          {sentChannel === 'phone' && (
            <div style={{ marginBottom: 14 }}>
              <V2Input
                label="Verification code"
                value={otpCode}
                onChange={v => { setOtpCode(v.replace(/\D/g, '').slice(0, 6)); setVerifyError(null); }}
                placeholder="123456"
                type="tel"
                autoComplete="one-time-code"
                onboardingField="auth_otp_code"
                error={verifyError}
                autoFocus
              />
              <V2Btn onClick={handleVerifyOtp} loading={verifyLoading}>
                Verify code
              </V2Btn>
            </div>
          )}

          <V2GhostBtn
            onClick={() => { setView('form'); setOtpCode(''); setVerifyError(null); }}
            style={{ marginTop: sentChannel === 'phone' ? 4 : 0 }}
          >
            Didn't receive it? Try again
          </V2GhostBtn>
        </>
      ) : (
        <>
          {/* Google at top */}
          <button
            onClick={handleGoogle}
            disabled={googleLoading}
            style={{
              width: '100%', height: 52, borderRadius: 14, border: `1.5px solid ${V2C.lineHard}`,
              background: '#fff', color: V2C.ink, opacity: googleLoading ? 0.6 : 1,
              fontFamily: fontBody, fontSize: 15, fontWeight: 600,
              cursor: googleLoading ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              transition: 'all 0.2s', marginTop: 18,
            }}
          >
            {googleLoading ? <SpinnerIcon size={18} color={V2C.mute} /> : <GoogleGlyph />}
            {googleLoading ? 'Redirecting to Google…' : 'Continue with Google'}
          </button>

          {error && !showEmailPhoneAuth && (
            <div role="alert" style={{ marginTop: 8 }}>
              <V2InlineMsg kind="error">{error}</V2InlineMsg>
            </div>
          )}

          {showEmailPhoneAuth && (
            <>
              {/* OR divider */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, margin: '14px 0',
                color: V2C.muteSoft, fontSize: 11, fontWeight: 600, letterSpacing: 1,
                fontFamily: fontBody,
              }}>
                <div style={{ flex: 1, height: 1, background: V2C.line }} />
                OR
                <div style={{ flex: 1, height: 1, background: V2C.line }} />
              </div>

              {/* Single auto-detect field */}
              <V2Input
                label="Email or phone"
                type={idKind === 'phone' ? 'tel' : 'text'}
                autoComplete={idKind === 'phone' ? 'tel' : 'email'}
                onboardingField="auth_identifier"
                value={input}
                onChange={onInputChange}
                placeholder="you@example.com or (555) 123-4567"
                error={error}
                suffix={detectedPill}
                hint={!error && (idKind === 'email' || idKind === 'phone')
                  ? `We'll send your magic link to your ${idKind === 'email' ? 'email' : 'phone'}.`
                  : undefined}
                autoFocus
              />

              <V2Btn onClick={handleSend} loading={loading}>
                {loading ? 'Signing in…' : 'Sign In'}
              </V2Btn>
            </>
          )}

          <div style={{
            fontSize: 11, color: V2C.muteSoft, textAlign: 'center',
            marginTop: 18, lineHeight: 1.55, fontFamily: fontBody,
          }}>
            By continuing you agree to Figgy's{' '}
            <span style={{ color: V2C.brand }}>Terms</span>{' '}and{' '}
            <span style={{ color: V2C.brand }}>Privacy</span>.
          </div>
        </>
      )}
    </V2Step>
  );
}
